import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  deleteDocument,
  duplicateDocument,
  fetchDocuments,
  updateDocument,
  type DocDocument,
} from '@/lib/documents';
import DocumentEditorModal from '@/pages/dashboard/apps/file-manager/components/DocumentEditorModal';

/**
 * Édition d'un document dans une page dédiée (et non en superposition).
 * URL : /dashboard/documents/:id
 */
export default function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [document, setDocument] = useState<DocDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const numeric = Number(id);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setError('Document introuvable.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('documents')
        .select('*')
        .eq('id', numeric)
        .maybeSingle();
      if (dbError) throw dbError;
      if (!data) throw new Error('Document introuvable ou supprimé.');
      setDocument(data as DocDocument);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger ce document.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (content: Record<string, unknown>, name: string) => {
    if (!document) return;
    setSaving(true);
    try {
      await updateDocument(document.id, content, name, user ? { id: user.id, name: user.name } : undefined);
      setDocument((prev) => (prev ? { ...prev, name, content, updated_at: new Date().toISOString() } : prev));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!document) return;
    await deleteDocument(document.id);
    navigate('/dashboard/documents', { replace: true });
  };

  const handleDuplicate = async (content: Record<string, unknown>, name: string) => {
    if (!document) return;
    const copy = await duplicateDocument({ ...document, content, name });
    navigate(`/dashboard/documents/${copy.id}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
        <p className="text-sm text-foreground-600">{error || 'Document introuvable.'}</p>
        <button
          onClick={() => navigate('/dashboard/documents')}
          className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-background-200/70"
        >
          Retour aux documents
        </button>
      </div>
    );
  }

  // L'éditeur occupe tout l'écran : le menu latéral du dashboard (z-50) ne
  // doit plus passer par-dessus et voler la place de la zone d'édition.
  return (
    <div className="fixed inset-0 z-[70] bg-white">
      <DocumentEditorModal
        document={document}
        saving={saving}
        currentUser={{ id: user?.id ?? 0, name: user?.name ?? '' }}
        onSave={handleSave}
        onClose={() => navigate('/dashboard/documents')}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
      />
    </div>
  );
}